import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'pending'

const TONE_CLASS: Record<Tone, string> = {
  neutral: '',
  ok: 'status__icon--ok',
  warn: 'status__icon--warn',
  bad: 'status__icon--bad',
  pending: 'status__icon--pending',
}

type StatusProps = {
  /** 24×24 line icon, sized down by `.status__icon svg`. */
  icon: ReactNode
  title: ReactNode
  /** Explanatory copy under the heading. */
  children?: ReactNode
  /** Buttons / links. Wrap two equal-weight ones in `.status__actions--pair`. */
  actions?: ReactNode
  tone?: Tone
  /** Set for failures the user did not ask to see, so SRs announce them. */
  alert?: boolean
}

/**
 * An outcome screen: glyph, heading, one paragraph, an action.
 *
 * Shares the form column — same anchor, same width — so a resolved page
 * reads as the same page having resolved rather than a different design.
 * Every terminal state in the app (no workspaces, load failure, crash,
 * 404, signed out) is one of these, which is why they all sit on the same
 * pixel row.
 */
export function Status({
  icon,
  title,
  children,
  actions,
  tone = 'neutral',
  alert = false,
}: StatusProps) {
  const headingId = useId()

  return (
    <section
      className="status"
      aria-labelledby={headingId}
      role={alert ? 'alert' : undefined}
    >
      <div className="navslot" />
      <span className={cn('status__icon', TONE_CLASS[tone])} aria-hidden="true">
        {icon}
      </span>
      <h1 id={headingId}>{title}</h1>
      {children ? <p className="sub">{children}</p> : null}
      {actions ? <div className="status__actions">{actions}</div> : null}
    </section>
  )
}
